import { useWatch } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import {
  forwardRef,
  RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  SaveButton,
  useDrawerState,
  UseDrawerStateReturn,
} from 'ui'
import { create } from 'vest'
import {
  MoreForLess,
  MoreForLessBenefit,
  MoreForLessBenefitVariant,
  MoreForLessTrigger,
  type MoreForLessEffect,
} from '../../../../../typings'
import { isValid } from '../../../../utils'
import { AdvancedConfig } from './AdvancedConfig'
import { BenefitEditor } from './Benefit'
import { benefitValidationSuite } from './Benefit/validation'
import { messages } from './messages'
import { EditorTrigger } from './Trigger'
import { triggerValidationSuite } from './Trigger/validation'

type Props = {
  state: UseDrawerStateReturn
  initialConfig: MoreForLessEffect
  onSave: (config: NonNullableObject<MoreForLessEffect>) => void
}

const BENEFIT_ADVANCED_CONFIG = 'benefit.apply'

export type MoreForLessTriggerEditor = Omit<
  MoreForLessTrigger,
  'minQuantity'
> & {
  minQuantity: number | undefined
}

export type MoreForLessBenefitEditor = Omit<MoreForLessBenefit, 'quantity'> & {
  quantity: number | undefined
}

export type MoreForLessEffectEditor = {
  [MoreForLess.Trigger]: MoreForLessTriggerEditor
  [MoreForLess.Benefit]: MoreForLessBenefitEditor
}

export const EMPTY_MORE_FOR_LESS_TRIGGER: MoreForLessTriggerEditor = {
  skus: [],
  collections: [],
  minQuantity: undefined,
}

export const EMPTY_MORE_FOR_LESS_BENEFIT: MoreForLessBenefitEditor = {
  quantity: undefined,
  discount: {
    type: MoreForLessBenefitVariant.PERCENTUAL,
    value: undefined,
  },
  apply: {
    equals: false,
    mostExpensive: false,
    distributed: false,
  },
}

export const EMPTY_MORE_FOR_LESS_EFFECT: MoreForLessEffectEditor =
  Object.freeze({
    [MoreForLess.Trigger]: EMPTY_MORE_FOR_LESS_TRIGGER,
    [MoreForLess.Benefit]: EMPTY_MORE_FOR_LESS_BENEFIT,
  })

function EditorMoreForLessComponent(
  props: Props,
  ref: RefObject<EditorRefType>
) {
  const { initialConfig, onSave, state } = props
  const { open, hide } = state
  const intl = useIntl()

  const isFilled = useCallback(
    (
      config: MoreForLessEffect
    ): config is NonNullableObject<MoreForLessEffect> =>
      !!config.trigger && !!config.benefit,
    []
  )

  const config = useMemo(
    () =>
      isFilled(initialConfig)
        ? initialConfig
        : { ...EMPTY_MORE_FOR_LESS_EFFECT },
    [initialConfig, isFilled]
  )

  const validation = create((values: NonNullableObject<MoreForLessEffect>) => {
    triggerValidationSuite(intl, MoreForLess.Trigger)(values)
    benefitValidationSuite(intl, MoreForLess.Benefit)(values)
  })

  const { drawerForm } = useDrawerSubForm<MoreForLessEffectEditor>({
    config,
    isOpen: open,
    validationFn: validation,
  })

  const drawerState = useDrawerState()

  useImperativeHandle(ref, () => ({
    openAdvancedDrawer: () => drawerState.show(),
  }))

  const trigger = useWatch({ form: drawerForm, name: MoreForLess.Trigger })

  const handleSave = async () => {
    const valid = await drawerForm.trigger()
    const values = drawerForm.getValues()

    if (!isValid<NonNullableObject<MoreForLessEffect>>(values, valid)) {
      return
    }

    onSave(values)
    state.hide()
  }

  const handleAdvancedConfigSave = (newApply) => {
    drawerForm.setValue(BENEFIT_ADVANCED_CONFIG, newApply as never)
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} />
        </DrawerTitle>
        <DismissButton onHide={hide} />
      </DrawerHeader>
      <DrawerContent>
        <EditorTrigger name={MoreForLess.Trigger} form={drawerForm} />
        <BenefitEditor
          name={MoreForLess.Benefit}
          form={drawerForm}
          trigger={trigger}
        />
      </DrawerContent>
      <DrawerFooter>
        <AdvancedConfig
          form={drawerForm}
          onSave={handleAdvancedConfigSave}
          drawerState={drawerState}
        />
        <CancelButton onClick={hide} />
        <SaveButton onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}

export interface EditorRefType {
  openAdvancedDrawer: () => void
}

export const EditorMoreForLess = forwardRef<EditorRefType, Props>(
  EditorMoreForLessComponent
)
